/**
 * A locked backup picked for import (decision 72; part 41b log, E11–E14, BW7, BW8): Import bundle…'s picker also takes
 * `.cockatrice-backup`, and a file whose first bytes are a locked backup's asks for its backup password in a sheet. It is
 * opened in a KDF worker of its own, then goes on to the bundle import exactly as a picked `.zip` (parts 24b–24d). A wrong
 * password is said and tried again, with no limit (E12: local, Argon2id per try); any other refusal says why (E13).
 * Cancel, Close or Escape hand nothing on, even when the file opens after.
 */
import { ModalBody, ModalButtons } from './modal.ts';
import { BACKUP_MAGIC, isLockedBackup } from '@cockatrice/crypto';
import { createSignal, onCleanup } from 'solid-js';
import { LOCKED_ENDING } from '../backup/lock.ts';
import type { OpenedBackup } from '../kdf/host.ts';
import { ErrorText, focusField } from './account-fields.ts';
import { SheetHead } from './account-sheet.ts';
import { focusOn } from './announce.ts';
import { el } from '../ui/dom.ts';
import type { Child } from '../ui/dom.ts';
import Button from '../ui/Button.ts';
import Icon from '../ui/Icon.ts';
import PasswordField from '../ui/PasswordField.ts';
import { component } from '../ui/solid.ts';
import type { Shell } from './shell.ts';

/** E11, E14: whether `file` starts as a locked backup does (a `.zip` doesn't); one that can't be read is taken as not. */
export async function isLocked(file: Blob): Promise<boolean> {
  try {
    return isLockedBackup(new Uint8Array(await file.slice(0, BACKUP_MAGIC.length).arrayBuffer()));
  } catch {
    // The bundle import reads it again and says why it can't (part 24c).
    return false;
  }
}

/** E11: the opened bundle's file name: the locked file's, `.cockatrice-backup` made `.zip` (the review names it). */
export function openedName(name: string): string {
  if (name.toLowerCase().endsWith(LOCKED_ENDING)) return `${name.slice(0, -LOCKED_ENDING.length)}.zip`;
  return name.toLowerCase().endsWith('.zip') ? name : `${name}.zip`;
}

/** E12, E13: why a locked backup wasn't opened, in words. */
export function refusalText(shell: Pick<Shell, 't'>, reason: Extract<OpenedBackup, { readonly kind: 'refused' }>['reason']): string {
  const { t } = shell;
  switch (reason) {
    case 'wrong-passphrase':
      return t('bundleImport.wrongPassword');
    case 'newer':
      return t('bundleImport.lockedNewer');
    case 'not-backup':
      return t('bundleImport.notBackup');
    case 'malformed':
    case 'too-large':
    case 'damaged':
      return t('bundleImport.lockedDamaged');
    case 'failed':
      return t('bundleImport.openFailed');
  }
}

/** Y1, E11, E14: a picked file to the bundle import: a locked one asks for its password first, a `.zip` goes straight on. */
export async function pickBundle(shell: Shell, file: File): Promise<void> {
  if (await isLocked(file)) {
    shell.ui.setDialog({ kind: 'locked-bundle', file });
    return;
  }
  void shell.restorer.pick(file);
  // AC′1: on the status's action, which keeps the focus through every phase without anything moving it.
  focusOn(shell, '.bundle-import-action');
}

/** E11–E13: the sheet asking for a locked backup's password; opened, its bundle goes to the import as a picked one. */
export function LockedBundleSheet(shell: Shell, file: File): Child {
  const { t, ui, restorer } = shell;
  const [password, setPassword] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal('');
  // E11: closed any way meanwhile, an open still under way hands nothing on.
  let shown = true;
  onCleanup(() => {
    shown = false;
  });
  const submit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    if (busy()) return;
    if (password() === '') {
      setError(t('account.error.password'));
      focusField('locked-pw');
      return;
    }
    setBusy(true);
    setError('');
    const opened = await shell.openBackup(file, password());
    if (!shown) return;
    setBusy(false);
    if (opened.kind === 'opened') {
      ui.setDialog(null);
      void restorer.pick(new File([opened.file], openedName(file.name), { type: 'application/zip' }));
      focusOn(shell, '.bundle-import-action');
      return;
    }
    setError(refusalText(shell, opened.reason));
    focusField('locked-pw');
  };
  // The part log's accessibility list: the first field takes the focus once the sheet is open.
  queueMicrotask(() => {
    focusField('locked-pw');
  });
  return [
    SheetHead(shell, () => t('bundleImport.reviewTitle'), () => t('bundleImport.lockedTitle')),
    el(
      'form',
      { class: 'c-modal__form locked-bundle', noValidate: true, onSubmit: (event: SubmitEvent) => void submit(event) },
      ModalBody(
        {},
        el('p', { class: 'ac-lead' }, t('bundleImport.lockedLead', { name: file.name })),
        component(PasswordField, {
          id: 'locked-pw',
          label: t('lock.field'),
          autocomplete: 'current-password',
          // The form checks it itself (an empty one is said on the sheet), so no "Required" beside the one field.
          'aria-required': 'true',
          // The sheet's error, said once below the form (`ErrorText`), describes the field.
          'aria-describedby': 'sheet-error',
          get invalid() {
            return error() !== '';
          },
          onChange: (value: string) => {
            setPassword(value);
          },
        }),
        el('p', { class: 'ac-hint lock-hint' }, component(Icon, { name: 'lock', size: 'sm', class: 'lock-hint__icon' }), ' ', t('lock.checkHint')),
      ),
      ModalButtons(
        component(Button, { variant: 'ghost', class: 'sheet-cancel', onClick: () => ui.setDialog(null), children: t('account.sheet.cancel') }),
        component(Button, {
          variant: 'primary',
          type: 'submit',
          // Argon2id takes about a second on a phone: the button says it is working and keeps its place meanwhile.
          get loading() {
            return busy();
          },
          get children() {
            return busy() ? t('bundleImport.opening') : t('bundleImport.unlock');
          },
        }),
      ),
    ),
    el('div', { class: 'c-modal__error' }, ErrorText('sheet-error', error)),
  ];
}
